/**
 * `studio/analytics.html` §4 — tier mix, package mix, pipeline.
 *
 * One region, three columns, no borders between them (CONTRACT.md §2.2 and §4.4).
 * Each column opens with a `.hero-cap`, which §5 documents as naming "a figure **or a
 * small block**" — this is the case that documentation was written for.
 *
 * **Tier mix is sequential, not categorical.** `--s6/--s4/--s2` rather than
 * `--c1..--c4`: three tiers are one ordered scale, and the categorical ramp is spent
 * on the four billing models in §2.
 *
 * **The pipeline never sums its two figures.** `AnalyticsPipelineSchema` is explicit:
 * "a one-time total and a package change's $/mo delta are different units and are
 * never summed into one figure." So the caption prints both, side by side, with the
 * unit on each. `analytics.html`'s fifth pipeline row is a **lead** — Meridian Coffee
 * Roasters — and `.docs/00-index.md` §6 rules leads out of scope ("No CRM. Pipeline =
 * proposed work orders + proposed retainer changes"), so there is no row for one.
 */

import {
  Chart,
  Legend,
  LegendKey,
  ProportionBar,
  PROPORTION_VIEW_BOX,
} from '../../../components/portal/Chart.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { FileIcon } from '../../../components/portal/Files.js'
import { Kv, KvRow } from '../../../components/portal/Kv.js'
import {
  Push,
  Row,
  ScrollX,
  Section,
  SectionHead,
  Stack,
} from '../../../components/portal/Layout.js'
import { Region, RegionCols } from '../../../components/portal/Region.js'
import { Table, TBody, Td, Th, THead, Tr } from '../../../components/portal/Table.js'
import { B, Muted, Num, Ref, Small, Xs } from '../../../components/portal/Text.js'
import { ClientLink } from '../../../components/studio/ClientLink.js'
import { HeroCapInfo } from '../../../components/studio/HeroCapInfo.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import type { AnalyticsReads, PackageMixData, PipelineData, TierMixData } from './data.js'
import {
  pipelineAmountLabel,
  SHAPE_LABEL,
  TIER_SLOT,
  TIER_SLOT_FALLBACK,
  type PipelineItem,
} from './labels.js'
import { Panel } from './Panel.js'
import { WHOLE_AMOUNT } from './render.js'
import { share } from '../../../format/percent.js'
import { onScreen, tierMixTable } from './tables.js'

export type MixSectionProps = {
  readonly tierMix: AnalyticsReads['tierMix']
  readonly packageMix: AnalyticsReads['packageMix']
  readonly pipeline: AnalyticsReads['pipeline']
}

export function MixSection({ tierMix, packageMix, pipeline }: MixSectionProps) {
  return (
    <Section>
      {/* None of these three reads takes a period — they are today's picture, and
          the head says so rather than borrowing the seg's month. */}
      <SectionHead title="Mix and pipeline" note="As things stand today" pushNote />
      <Region>
        <RegionCols>
          <Stack gap="tight">
            <HeroCapInfo
              label="Market tier · share of MRR"
              tip="How recurring revenue is split across market tiers (enterprise, mid-market, small). One ordered scale — not four unrelated colours."
            />
            <Panel query={tierMix} subject="the tier mix">
              {(data) => <TierMix tiers={data} />}
            </Panel>
          </Stack>

          <Stack gap="tight">
            <HeroCapInfo
              label="Package mix"
              tip="Which retainer packages the live book is on, and how much MRR each package shape carries."
            />
            <Panel query={packageMix} subject="the package mix">
              {(data) => <PackageMix mix={data} />}
            </Panel>
          </Stack>

          <Panel query={pipeline} subject="the pipeline">
            {(data) => <Pipeline pipeline={data} />}
          </Panel>
        </RegionCols>
      </Region>
    </Section>
  )
}

/* ------------------------------------------------------------------ tier mix */

function TierMix({ tiers }: { readonly tiers: TierMixData }) {
  const format = useFormat()
  const rows = tiers.tiers

  return (
    <Chart
      conclusion={
        rows.length === 0
          ? 'No live retainer, so there is no MRR to split by tier.'
          : `MRR by market tier: ${rows
              .map(
                (one) =>
                  `Tier ${format.number(one.tier)} ${format.money(one.mrr, WHOLE_AMOUNT)}, ${share(format, one.share)}`,
              )
              .join('; ')}.`
      }
      viewBox={PROPORTION_VIEW_BOX}
      legend={
        <Legend>
          {rows.map((one) => (
            <LegendKey key={one.tier} slot={TIER_SLOT[one.tier] ?? TIER_SLOT_FALLBACK}>
              {`Tier ${format.number(one.tier)}`}
            </LegendKey>
          ))}
        </Legend>
      }
      note="Every amount here is the USD equivalent of what that tier's clients pay in their own currency."
      table={onScreen(tierMixTable(tiers, format))}
    >
      <ProportionBar
        startLabel={format.money({ amount: 0, currency: tiers.total.currency }, WHOLE_AMOUNT)}
        endLabel={format.money(tiers.total, WHOLE_AMOUNT)}
        parts={rows.map((one, index) => ({
          label: `Tier ${String(one.tier)}`,
          value: one.mrr.amount,
          slot: TIER_SLOT[one.tier] ?? TIER_SLOT_FALLBACK,
          title: `Tier ${format.number(one.tier)} — ${format.money(one.mrr, WHOLE_AMOUNT)}, ${share(format, one.share)}`,
          inside:
            index === 0
              ? `Tier ${format.number(one.tier)} · ${share(format, one.share)}`
              : share(format, one.share),
        }))}
      />
    </Chart>
  )
}

/* --------------------------------------------------------------- package mix */

function PackageMix({ mix }: { readonly mix: PackageMixData }) {
  const format = useFormat()

  return (
    <>
      <ScrollX>
        <Table>
          <THead>
            <Tr>
              <Th>Shape</Th>
              <Th right>Clients</Th>
              <Th right>MRR</Th>
              <Th right>Blocks</Th>
            </Tr>
          </THead>
          <TBody>
            {mix.rows.map((row) => (
              <Tr key={row.shape}>
                <Td>
                  <B>{SHAPE_LABEL[row.shape]}</B>
                  <Xs block>
                    <Muted>
                      {row.clients.map((one, index) => (
                        <span key={one.client}>
                          {index === 0 ? null : ', '}
                          <ClientLink client={one.client} name={one.name} />
                        </span>
                      ))}
                    </Muted>
                  </Xs>
                </Td>
                <Td right num>
                  {format.number(row.clients.length)}
                </Td>
                <Td right num>
                  {format.money(row.mrr, WHOLE_AMOUNT)}
                </Td>
                <Td right num>
                  {format.number(row.blocks)}
                </Td>
              </Tr>
            ))}
            <Tr variant="total">
              <Td>All</Td>
              <Td right num>
                {format.number(mix.total.clients)}
              </Td>
              <Td right num>
                {format.money(mix.total.mrr, WHOLE_AMOUNT)}
              </Td>
              <Td right num>
                {format.number(mix.total.blocks)}
              </Td>
            </Tr>
          </TBody>
        </Table>
      </ScrollX>

      <Small>
        <Muted>{shapeNote(mix, format)}</Muted>
      </Small>

      <Fold summary={`How the ${format.number(mix.total.blocks)} blocks break down`}>
        <Kv bare>
          <KvRow
            k="Average blocks per client"
            v={<Num>{format.number(mix.average_blocks_per_client, { fractionDigits: 1 })}</Num>}
          />
          <KvRow
            k="Clients at the bundle-discount band"
            v={<Num value={mix.clients_at_bundle_band} />}
          />
          <KvRow k="Clients at the one-block minimum" v={<Num value={mix.clients_at_minimum} />} />
          <KvRow
            k="Most common block"
            v={
              mix.most_common_block === null
                ? '—'
                : `${mix.most_common_block.service} · ${format.number(mix.most_common_block.clients)} clients`
            }
          />
        </Kv>
      </Fold>
    </>
  )
}

/**
 * The package-mix panel's closing line — how much of the book is bespoke.
 *
 * `analytics.html`'s own line here is "Presets are templates, not prices — both
 * preset clients would be quoted the same way if they walked in today", which is
 * policy and belongs behind §3's one fold (DESIGN.md P7). This is the fact under it:
 * a count of the rows immediately above, which is the derivation DESIGN.md
 * §Non-negotiable permits.
 */
function shapeNote(mix: PackageMixData, format: Formatter): string {
  const custom = mix.rows.find((row) => row.shape === 'custom')?.clients.length ?? 0
  if (mix.total.clients === 0) return 'No client is on a live retainer.'
  return `${format.number(custom)} of ${format.number(mix.total.clients)} clients are on a package built for them; the rest are on a preset.`
}

/* ------------------------------------------------------------------ pipeline */

function Pipeline({ pipeline }: { readonly pipeline: PipelineData }) {
  return (
    <Stack gap="tight">
      <PipelineCaption pipeline={pipeline} />
      {pipeline.items.length === 0 ? (
        <Empty>Nothing is quoted and undecided.</Empty>
      ) : (
        pipeline.items.map((item) => <PipelineRow key={item.ref} item={item} />)
      )}
      <Small>
        <Muted>Everything here is with the client and has not been answered.</Muted>
      </Small>
    </Stack>
  )
}

function PipelineCaption({ pipeline }: { readonly pipeline: PipelineData }) {
  const format = useFormat()
  return (
    <HeroCapInfo
      label={pipelineCaption(pipeline, format)}
      tip="Quoted work still with the client and unanswered. One-time totals and monthly deltas are different units — they are never added into one figure."
    />
  )
}

/** "Pipeline · $6,030 quoted · +$800 a month proposed" — two units, never added. */
function pipelineCaption(pipeline: PipelineData, format: Formatter): string {
  const quoted = `Pipeline · ${format.money(pipeline.total, WHOLE_AMOUNT)} quoted`
  if (pipeline.monthly_delta.amount === 0) return quoted
  const sign = pipeline.monthly_delta.amount > 0 ? '+' : ''
  return `${quoted} · ${sign}${format.money(pipeline.monthly_delta, WHOLE_AMOUNT)} a month proposed`
}

function PipelineRow({ item }: { readonly item: PipelineItem }) {
  const format = useFormat()
  const project = item.kind === 'project'

  return (
    <Row top>
      <FileIcon>{project ? 'Q' : '+'}</FileIcon>
      <div>
        {/*
         * `mockups/studio/analytics.html`: "Northbay Dental — Website M". The client
         * and then the work, in the bold line — which is why `title` no longer has the
         * client in it and `name` is on the wire beside it. It printed
         * `Package change — casaturcacasaturca` before: the read model put the slug in
         * the title and the page linked the slug again underneath.
         */}
        <B>
          <ClientLink client={item.client} name={item.name} /> — {item.title}
        </B>
        <Xs block>
          <Muted>
            <Ref value={item.ref} /> · {project ? 'quote' : 'package change'} ·{' '}
            {item.proposed_at === null
              ? 'drafted, not yet sent'
              : `proposed ${format.date(item.proposed_at)}`}
          </Muted>
        </Xs>
      </div>
      <Push />
      <Num>{pipelineAmountLabel(item, format)}</Num>
    </Row>
  )
}
